import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import AdjustIcon from '@mui/icons-material/Adjust';
import CancelIcon from '@mui/icons-material/Cancel';
import { Button, Chip, ChipPropsColorOverrides, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';

import { EntityMetadataModel, EnumMetadataModel, ExpressionMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import useAsyncMemo from '../../../../common/hooks/useAsyncMemo';
import { sortByOrder } from '../../../../common/sortByOrder';
import { useContextMenu } from '../../../../components/ContextMenuContext';
import { useExpressionMetadata } from '../../../../components/ExpressionMetadataContext';
import { codeFontFamily } from '../../../../themes/codeTokens';
import { ParameterTypes } from '@@common';
import { ExpressionCategories } from '../../Models/ExpressionCategories';
import { ExpressionModel, PRIMITIVE_TYPES_WITHOUT_REF } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { JsonSchemaModel } from '../../Models/JsonSchemaModel';
import { StringTemplateExpressionModel } from '../../Models/StringTemplateModel';
import { VarExpressionModel } from '../../Models/VarExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import { getCollectionExpressionLabel } from '../../Models/getCollectionExpressionLabel';
import { getExpressionType } from '../../Models/getExpressionType';
import { resolveExpectedObjectType } from '../../Services/ExpectedObjectTypeResolver';
import { useParameterValueOptions } from '../../Services/parameterValueOptions';
import ParameterPlaceholder from '../Parameters/ParameterPlaceholder';
import ExpressionPlaceholderCollectionInline from './ExpressionPlaceholderCollectionInline';
import ExpressionPlaceholderCollectionObjectMapInline from './ExpressionPlaceholderCollectionObjectMapInline';
import ExpressionPlaceholderCollectionParamEditor from './ExpressionPlaceholderCollectionParamEditor';
import ExpressionPlaceholderObjectInline from './ExpressionPlaceholderObjectInline';
import ExpressionPlaceholderPopover from './ExpressionPlaceholderPopover';
import ExpressionSchemaPlaceholder from './ExpressionSchemaPlaceholder';
import ExpressionSuggestionShortcuts from './ExpressionSuggestionShortcuts';
import { resolveVarTargetScope } from './expressionPlaceholderUtils';
import { shouldShowCollectionEditor, shouldShowObjectEditor } from './shouldShowObjectEditor';

/** Excluded from the expression type picker list (handled inline, not as generic expressions). */
const EXPRESSION_PICKER_EXCLUDE: string[] = [ExpressionTypes.Const];

/** Excluded from the generic parameter-tree chip rendering (have their own custom chip display). */
const EXPRESSION_CHIP_LABEL_EXCLUDE: string[] = [ExpressionTypes.Const, ExpressionTypes.StringTemplate];

const CHIP_COLOR_BY_EXPRESSION_TYPE: Record<
	string,
	OverridableStringUnion<'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning', ChipPropsColorOverrides>
> = {
	[ExpressionTypes.Const]: 'success',
	[ExpressionTypes.Var]: 'info',
	[ExpressionTypes.Object]: 'info',
	[ExpressionTypes.Collection]: 'info',
	[ExpressionTypes.StringTemplate]: 'success',
	[ExpressionTypes.ContentTemplate]: 'success',
};

const CHIP_ACTION_SLOT_STYLE: React.CSSProperties = {
	width: '20px',
	height: '20px',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'pointer',
	flexShrink: 0,
	lineHeight: 0,
};

const CHIP_WRAP_ICON_SX = {
	fontSize: 18,
	color: 'primary.main',
	display: 'block',
	'&:hover': {
		color: 'primary.dark',
	},
};

const CHIP_REMOVE_ICON_SX = {
	fontSize: 18,
	color: 'error.main',
	display: 'block',
	'&:hover': {
		color: 'error.dark',
	},
};

const CONTENT_TEMPLATE_EXPRESSION_METADATA: ExpressionMetadataModel = {
	name: ExpressionTypes.ContentTemplate,
	displayName: 'Content Template',
	category: 'Content Service',
	categories: [ExpressionCategories.DesignerComplex],
	description: 'Renders a template from the content service.',
	parameters: [
		{
			name: 'TemplateId',
			displayName: 'Template',
			order: 0,
			type: ParameterType.Primitive,
			typeCustom: ParameterTypes.ContentTemplateId,
			categories: [],
			isCollection: false,
			expectedType: { type: PrimitiveType.Guid },
		},
		{
			name: 'Input',
			displayName: 'Input',
			order: 1,
			type: ParameterType.Expression,
			categories: [],
			isCollection: false,
			expectedType: { typeRef: 'TemplateId' },
		},
	],
	resultType: { type: PrimitiveType.String },
	iconName: 'Article',
};

const COLLECTION_EXPRESSION_METADATA: ExpressionMetadataModel = {
	name: ExpressionTypes.Collection,
	displayName: 'Collection',
	category: ExpressionCategories.DesignerComplex,
	categories: [ExpressionCategories.DesignerComplex],
	description: 'Defines a static list of expression values.',
	parameters: [],
	resultType: { type: PrimitiveType.Collection },
	iconName: 'List',
};

function normalizeExpressionMetadata(metadata: ExpressionMetadataModel | undefined, expressionType: string | undefined): ExpressionMetadataModel | undefined {
	if (expressionType !== ExpressionTypes.ContentTemplate) {
		return metadata;
	}

	if (!metadata || metadata.parameters.length === 0) {
		return CONTENT_TEMPLATE_EXPRESSION_METADATA;
	}

	return metadata;
}

function GetChipColor(
	expression: ExpressionModel,
): OverridableStringUnion<'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning', ChipPropsColorOverrides> {
	if (!expression) return 'default';
	if (expression.Type === ExpressionTypes.Var) {
		const varExpression = expression as VarExpressionModel;
		if (varExpression.Target != null) {
			return 'default';
		}
	}
	return CHIP_COLOR_BY_EXPRESSION_TYPE[expression.Type] || 'default';
}

function shouldDisplayVerticalParameterLabel(param: ParameterMetadata, parameterCount: number): boolean {
	return !(parameterCount === 1 && param.name.toLowerCase() === 'value');
}

type ExpressionChipActionsProps = {
	isColumnLayout: boolean;
	onWrap: React.MouseEventHandler<HTMLElement>;
	onRemove: () => void;
};

function ExpressionChipActions({ isColumnLayout, onWrap, onRemove }: ExpressionChipActionsProps) {
	return (
		<Stack
			direction="row"
			spacing={0.25}
			alignItems="center"
			onClick={(e) => e.stopPropagation()}
			sx={
				isColumnLayout
					? {
							position: 'absolute',
							top: 0,
							right: 0,
						}
					: undefined
			}
		>
			<span className="plus-icon-container" onClick={onWrap} title="Wrap in expression" style={CHIP_ACTION_SLOT_STYLE}>
				<AdjustIcon sx={CHIP_WRAP_ICON_SX} />
			</span>
			<span title="Remove expression" style={CHIP_ACTION_SLOT_STYLE}>
				<CancelIcon sx={{ ...CHIP_REMOVE_ICON_SX, cursor: 'pointer' }} onClick={onRemove} />
			</span>
		</Stack>
	);
}

export type ExpressionPlaceholderProps = {
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel | undefined) => void;
	parameterMetadata: ParameterMetadata;
	parameterEnum?: EnumMetadataModel; // when set, const input renders a radio group of enum values
	variableScope?: IVariableScope; // Input scope: variables available when editing this expression's parameters
	isEditable?: boolean;
	expectedObjectType?: EntityMetadataModel; // used to determine if the expression is an object expression
	preferExpressionPicker?: boolean;
	contextMenuId?: string; // shared context menu ID for nested expressions
	/** When provided, restricts the expression type picker and value input to these primitive types only. */
	allowedResultTypes?: PrimitiveType[];
	/// The containing activity/model, used to resolve service-connection property options.
	model?: Record<string, any>;
	/** Let literal values wrap across the value column in object editors. */
	fullWidth?: boolean;
};

export default function ExpressionPlaceholder(props: ExpressionPlaceholderProps) {
	const schema = props.parameterMetadata.expectedType?.typeJsonSchema as JsonSchemaModel | undefined;
	if (schema && props.isEditable && !props.parameterMetadata.expectedExpressionIsAssignable && !props.preferExpressionPicker) {
		return <ExpressionSchemaPlaceholder {...props} schema={schema} renderValue={(valueProps) => <ExpressionValuePlaceholder {...valueProps} />} />;
	}
	return <ExpressionValuePlaceholder {...props} />;
}

function ExpressionValuePlaceholder({
	expression,
	onExpressionChange,
	parameterMetadata,
	variableScope,
	isEditable,
	expectedObjectType,
	preferExpressionPicker,
	contextMenuId,
	parameterEnum,
	allowedResultTypes,
	model,
	fullWidth,
}: ExpressionPlaceholderProps) {
	const suggestedExpressions = parameterMetadata.suggestedExpressions;
	// Get expression metadata and scope calculator from context
	const { expressions: expressionMetadataList, expressionScopeCalculator } = useExpressionMetadata();

	const [expressionPending, setExpressionPending] = useState<ExpressionModel | undefined>(expression);

	// Use provided contextMenuId (shared across expression tree) or generate unique ID for this component instance (root level)
	const sharedContextMenuId = useMemo(() => contextMenuId || `expression-${Math.random().toString(36).substr(2, 9)}`, [contextMenuId]);

	// Each component gets its own unique ID for ownership, but shares the context menu ID
	const componentId = useMemo(() => `${sharedContextMenuId}-${Math.random().toString(36).substr(2, 9)}`, [sharedContextMenuId]);

	// Separate ID for the expression editing popover
	const expressionPopoverId = useMemo(() => `${componentId}-expression-popover`, [componentId]);

	// Use global context menu state - get both old and new methods
	const { openPopover, closePopover, isPopoverOwner, openContextMenu, closeContextMenu, isMenuOwner } = useContextMenu();

	const expressionType = useMemo(() => getExpressionType(expression), [expression]);
	const getInitialTabValue = useCallback(() => {
		if (parameterMetadata.expectedExpressionIsAssignable || preferExpressionPicker) {
			return 1;
		}
		if (expressionType === ExpressionTypes.Const) {
			return 0;
		}
		return expressionType === ExpressionTypes.Var ? 1 : 2;
	}, [parameterMetadata.expectedExpressionIsAssignable, preferExpressionPicker, expressionType]);
	const { valueOptions, valueOptionsSource, hasLoadedValueOptionsSource } = useParameterValueOptions(parameterMetadata, model, isEditable === true);

	const expressionMetadata = useMemo<ExpressionMetadataModel | undefined>(
		() =>
			expressionType
				? normalizeExpressionMetadata(
						expressionMetadataList.find((x) => x.name === expressionType),
						expressionType,
					)
				: undefined,
		[expressionType, expressionMetadataList],
	);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = isPopoverOwner(expressionPopoverId) && Boolean(anchorEl);

	// Context menu state
	const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<HTMLElement | null>(null);
	const contextMenuOpen = isMenuOwner(componentId) && Boolean(contextMenuAnchorEl);

	// State to track if we're in wrap mode (opened from plus icon)
	const [isWrapMode, setIsWrapMode] = useState<boolean>(false);

	const handleExpAdd = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		openPopover(expressionPopoverId, sharedContextMenuId);
		event.stopPropagation();
	};
	const handleExpClick = (event: React.MouseEvent<HTMLElement>) => {
		setTabValue(getInitialTabValue());
		setAnchorEl(event.currentTarget);
		openPopover(expressionPopoverId, sharedContextMenuId);
		event.stopPropagation();
	};
	const handleExpDelete = () => {
		onExpressionChange?.(undefined);
		handleClose();
	};

	const handleClose = () => {
		setAnchorEl(null);
		setIsWrapMode(false);
		closePopover();
	};

	// Context menu handlers
	const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
		if (!isEditable) return;
		event.preventDefault();
		event.stopPropagation();
		setContextMenuAnchorEl(event.currentTarget);
		openContextMenu(componentId, sharedContextMenuId);
	};

	const handleContextMenuClose = () => {
		setContextMenuAnchorEl(null);
		closeContextMenu();
	};

	const handleWrapInExpression = () => {
		if (!expression) return;

		// Close context menu first
		handleContextMenuClose();

		// Open the expression popover in wrap mode
		// We need to set the anchor element to the context menu anchor for proper positioning
		setAnchorEl(contextMenuAnchorEl);
		setIsWrapMode(true);
		openPopover(expressionPopoverId, sharedContextMenuId);
	};

	const handleRedDotPopoverClose = () => {
		setAnchorEl(null);
		setIsWrapMode(false);
		closePopover();
	};

	const handleRedDotClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (open && isWrapMode) {
			handleRedDotPopoverClose();
		} else {
			setAnchorEl(event.currentTarget);
			setIsWrapMode(true);
			openPopover(expressionPopoverId, sharedContextMenuId);
		}
	};

	const handleWrapWithExpression = (wrapperExpressionMetadata: ExpressionMetadataModel) => {
		if (!expression) return;

		// Create new wrapper expression with current expression as first parameter
		const wrapperExpression: ExpressionModel = {
			Type: wrapperExpressionMetadata.name,
		};

		// Find the first expression parameter by order
		const firstParam = [...wrapperExpressionMetadata.parameters].filter((p) => p.type === ParameterType.Expression).sort(sortByOrder)[0];

		if (firstParam) {
			if (firstParam.isCollection) {
				wrapperExpression[firstParam.name] = [expression];
			} else {
				wrapperExpression[firstParam.name] = expression;
			}
		}

		onExpressionChange?.(wrapperExpression);
		handleClose();
	};

	const handleApply = () => {
		if (isWrapMode && expressionPending) {
			// Find the expression metadata for wrapping
			const wrapperExpressionMetadata = expressionMetadataList.find((x) => x.name === expressionPending.Type);
			if (wrapperExpressionMetadata) {
				handleWrapWithExpression(wrapperExpressionMetadata);
			}
		} else {
			onExpressionChange?.(expressionPending);
		}
		handleClose();
	};

	const handleExpressionSelected = (selectedExpressionMetadata: ExpressionMetadataModel) => {
		if (isWrapMode) {
			handleWrapWithExpression(selectedExpressionMetadata);
			return;
		}

		onExpressionChange?.({
			Type: selectedExpressionMetadata.name,
		});
		handleClose();
	};

	const matchingPrimitiveTypes = useMemo(() => {
		if (allowedResultTypes) return allowedResultTypes;
		if (!expressionScopeCalculator) return [];
		return expressionScopeCalculator.resolveExpectedPrimitiveTypes(parameterMetadata, expression, variableScope, PRIMITIVE_TYPES_WITHOUT_REF);
	}, [allowedResultTypes, parameterMetadata, expression, variableScope, expressionScopeCalculator]);

	// Single async pass: compute per-parameter scopes, per-item collection scopes,
	// and expected object types — all parameters processed in parallel.
	const expressionData = useAsyncMemo(async () => {
		const parameterScopes = new Map<string, IVariableScope | undefined>();
		const collectionItemScopes = new Map<string, (IVariableScope | undefined)[]>();
		const parameterExpectedObjectTypes = new Map<string, EntityMetadataModel | undefined>();

		if (!expression || !expressionMetadata || !expressionScopeCalculator) {
			return { parameterScopes, collectionItemScopes, parameterExpectedObjectTypes };
		}

		// Single O(N) forward pass for all parameter scopes — avoids the O(N²) cost of
		// calling calculateVariableScopeForParameter per param (each re-walks from index 0).
		if (variableScope) {
			try {
				const allScopes = await expressionScopeCalculator.calculateAllParameterScopes(expression, expressionMetadata, variableScope);
				allScopes.forEach((scope, name) => parameterScopes.set(name, scope));
			} catch (error) {
				console.error(`Error calculating parameter scopes for expression '${expression.Type}':`, error);
				expressionMetadata.parameters.forEach((param) => parameterScopes.set(param.name, variableScope));
			}
		}

		// Expected object types and collection item scopes are independent — run in parallel.
		await Promise.all(
			expressionMetadata.parameters.map(async (param) => {
				if (param.type === ParameterType.Expression && param.expectedType?.typeRef) {
					try {
						const objectType = await expressionScopeCalculator.resolveExpectedObjectType(param, expressionMetadata.parameters, expression);
						if (objectType) parameterExpectedObjectTypes.set(param.name, objectType);
					} catch {
						// ignore — degrade gracefully
					}
				}

				if (param.isCollection && param.type === ParameterType.Expression && variableScope) {
					try {
						const itemScopes = await expressionScopeCalculator.calculateVariableScopesForCollectionParam(
							expression,
							param,
							expressionMetadata,
							variableScope,
							parameterScopes.get(param.name), // pass pre-computed scope to avoid redundant forward scan
						);
						collectionItemScopes.set(param.name, itemScopes);
					} catch (error) {
						console.error(`Error calculating collection item scopes for '${param.name}':`, error);
						// Fall back to param scope (or base scope) for every item, consistent with the
						// per-param fallback above so the collection editor always has a usable scope.
						const fallbackScope = parameterScopes.get(param.name) ?? variableScope;
						const items = (expression[param.name] ?? []) as unknown[];
						collectionItemScopes.set(
							param.name,
							items.map(() => fallbackScope),
						);
					}
				}
			}),
		);

		return { parameterScopes, collectionItemScopes, parameterExpectedObjectTypes };
	}, [expression, expressionMetadata, variableScope, expressionScopeCalculator]);

	const parameterScopes = expressionData?.parameterScopes;
	const collectionItemScopes = expressionData?.collectionItemScopes;
	const parameterExpectedObjectTypes = expressionData?.parameterExpectedObjectTypes;
	const effectiveExpectedObjectType = useMemo(() => resolveExpectedObjectType(parameterMetadata, expectedObjectType), [parameterMetadata, expectedObjectType]);
	const effectiveParameterExpectedObjectTypes = useMemo(() => {
		const result = new Map<string, EntityMetadataModel | undefined>();
		expressionMetadata?.parameters.forEach((param) => {
			result.set(param.name, resolveExpectedObjectType(param, parameterExpectedObjectTypes?.get(param.name)));
		});
		return result;
	}, [expressionMetadata, parameterExpectedObjectTypes]);

	// expectedExpressionIsAssignable means this param is an output/assignment target (variable picker), not an object input.
	// Even if the typeRef resolves to an object schema, we must not render the object editor for assignment targets.
	const isObjectType =
		!parameterMetadata.expectedExpressionIsAssignable && (!!effectiveExpectedObjectType || parameterMetadata.expectedType?.type === PrimitiveType.Object);

	const isCollectionType =
		!parameterMetadata.expectedExpressionIsAssignable &&
		(parameterMetadata.expectedType?.typeWrapInCollection === true || parameterMetadata.expectedType?.typeJsonSchema?.type === 'array');

	const isCollectionObjectType = isCollectionType && isObjectType;
	const showCollectionMapEditor = isCollectionObjectType && (!expression || expressionType === ExpressionTypes.CollectionMap);

	// Determine if we should show the collection editor for this expression
	const showCollectionEditor = !showCollectionMapEditor && shouldShowCollectionEditor(expression, expressionType, isCollectionType);

	// Determine if we should show the object editor for this expression.
	// Collection wrappers around object schemas should render as collections of objects, not as a single object.
	const showObjectEditor = !preferExpressionPicker && !showCollectionMapEditor && !showCollectionEditor && shouldShowObjectEditor(expression, expressionType, isObjectType);

	// For parameter editing, we always use the input variable scope (variableScope)
	// This ensures that when editing parameters, users see variables from the outer/base scope
	const matchingExpressionMetadataList = useMemo(() => {
		const result = expressionMetadataList.filter(
			(x) =>
				parameterMetadata.type === ParameterType.Expression &&
				!EXPRESSION_PICKER_EXCLUDE.includes(x.name) &&
				(!isCollectionType || !x.resultType || x.resultType.type === PrimitiveType.Collection || x.resultType.typeWrapInCollection === true) &&
				(allowedResultTypes
					? !x.resultType?.type || allowedResultTypes.includes(x.resultType.type)
					: !parameterMetadata.expectedType?.type || !x.resultType?.type || x.resultType.type === parameterMetadata.expectedType?.type),
		);

		const allowsString =
			parameterMetadata.type === ParameterType.Expression &&
			!parameterMetadata.expectedExpressionIsAssignable &&
			(allowedResultTypes
				? allowedResultTypes.includes(PrimitiveType.String)
				: !parameterMetadata.expectedType?.type || parameterMetadata.expectedType.type === PrimitiveType.String);

		const normalizedResult = result.map((item) => normalizeExpressionMetadata(item, item.name) ?? item);
		let finalResult = normalizedResult;
		if (allowsString && !finalResult.some((item) => item.name === ExpressionTypes.ContentTemplate)) {
			finalResult = [...finalResult, CONTENT_TEMPLATE_EXPRESSION_METADATA];
		}

		if (
			(isCollectionType || parameterMetadata.expectedType?.type === PrimitiveType.Collection) &&
			!finalResult.some((item) => item.name === ExpressionTypes.Collection)
		) {
			finalResult = [...finalResult, COLLECTION_EXPRESSION_METADATA];
		}

		return finalResult;
	}, [expressionMetadataList, parameterMetadata, allowedResultTypes, isCollectionType]);

	// Check if any parameters are collections to determine layout direction
	const hasCollectionParameters = useMemo(
		() => expressionMetadata?.parameters.some((param) => param.isCollection && param.type === ParameterType.Expression) ?? false,
		[expressionMetadata],
	);

	// Give nested expression parameters their own rows so operators remain readable.
	const shouldUseColumnLayout = useMemo(
		() => isEditable && ((fullWidth && (expressionType === ExpressionTypes.Const || expressionType === ExpressionTypes.StringTemplate)) || hasCollectionParameters || expressionMetadata?.parameters.some((param) => param.type === ParameterType.Expression) || expressionMetadata?.categories.includes(ExpressionCategories.DesignerComplex)),
		[isEditable, fullWidth, expressionType, hasCollectionParameters, expressionMetadata],
	);

	const [tabValue, setTabValue] = useState(getInitialTabValue);
	const wasOpenRef = useRef(false);

	useEffect(() => {
		const justOpened = open && !wasOpenRef.current;
		wasOpenRef.current = open;
		if (!justOpened) return;

		// Popover just opened — reset the pending expression to the committed value and
		// initialise the tab so the user always starts from a consistent state.
		setExpressionPending(expression);

		if (isWrapMode) {
			// In wrap mode, only the expressions tab is available
			setTabValue(2);
		} else {
			setTabValue(getInitialTabValue());
		}
	}, [open, expression, expressionType, isWrapMode, getInitialTabValue]);

	// Close local anchor when global context menu state changes to a different menu
	useEffect(() => {
		if (!isMenuOwner(componentId) && contextMenuAnchorEl) {
			setContextMenuAnchorEl(null);
		}
	}, [componentId, contextMenuAnchorEl, isMenuOwner]);

	// Close local anchor when global popover state changes to a different popover
	useEffect(() => {
		if (!isPopoverOwner(expressionPopoverId) && anchorEl) {
			setAnchorEl(null);
			setIsWrapMode(false);
		}
	}, [expressionPopoverId, anchorEl, isPopoverOwner]);

	const expressionChipActions = isEditable ? (
		<ExpressionChipActions isColumnLayout={shouldUseColumnLayout ?? false} onWrap={handleRedDotClick} onRemove={handleExpDelete} />
	) : null;
	const suggestedExpressionButtons = (
		<ExpressionSuggestionShortcuts
			suggestions={suggestedExpressions}
			expressionMetadata={expressionMetadataList}
			onSelectExpressionType={(type) => onExpressionChange?.({ Type: type })}
			onSelectConstValue={(value) => onExpressionChange?.({ Type: ExpressionTypes.Const, Value: value })}
			onOpenConstEditor={(anchor) => {
				setExpressionPending(undefined);
				setTabValue(0);
				setAnchorEl(anchor);
				openPopover(expressionPopoverId, sharedContextMenuId);
			}}
		/>
	);

	return (
		<div style={shouldUseColumnLayout ? { width: '100%' } : !isEditable ? { display: 'inline-flex', width: 'max-content', flexShrink: 0 } : undefined}>
			{/* Only show object editor when: expression is undefined and expects object, OR expression Type is explicitly "Object" */}
			{showObjectEditor && isEditable && (
				<ExpressionPlaceholderObjectInline
					expression={expression}
					onExpressionChange={onExpressionChange}
					parameterMetadata={parameterMetadata}
					expectedObjectType={effectiveExpectedObjectType}
					variableScope={variableScope}
					onSelectExpression={handleExpClick}
				/>
			)}
			{/* Show collection editor when: expression is undefined and expects collection, OR expression Type is explicitly "Collection" */}
			{showCollectionMapEditor && isEditable && effectiveExpectedObjectType && (
				<ExpressionPlaceholderCollectionObjectMapInline
					expression={expression}
					onExpressionChange={onExpressionChange}
					parameterMetadata={parameterMetadata}
					variableScope={variableScope}
					expectedObjectType={effectiveExpectedObjectType}
				/>
			)}
			{showCollectionEditor && isEditable && (
				<ExpressionPlaceholderCollectionInline
					expression={expression}
					onExpressionChange={onExpressionChange}
					parameterMetadata={parameterMetadata}
					variableScope={variableScope}
					contextMenuId={sharedContextMenuId}
					expectedObjectType={effectiveExpectedObjectType}
					onSelectExpression={handleExpClick}
				/>
			)}
			{/* Show chip for all other cases when expression exists */}
			{!(showObjectEditor && isEditable) && !(showCollectionMapEditor && isEditable) && !(showCollectionEditor && isEditable) && expression && (
				<Chip
					onClick={isEditable ? handleExpClick : undefined}
					onContextMenu={handleContextMenu}
					data-context-menu-trigger="true"
					color={GetChipColor(expression)}
					sx={
						shouldUseColumnLayout
							? {
									width: '100%',
									minWidth: 0,
									maxWidth: '100%',
									height: 'auto',
									alignItems: 'stretch',
									bgcolor: 'background.default',
									color: 'text.primary',
									border: '1px solid',
									borderColor: 'divider',
									borderLeft: '3px solid',
									borderLeftColor: 'primary.main',
									borderRadius: 1.5,
									'&:hover': { bgcolor: 'background.default', borderColor: 'primary.main' },
									'& .MuiChip-label': {
										display: 'block',
										width: '100%',
										minWidth: 0,
										whiteSpace: 'normal',
										padding: 1.25,
										overflow: 'visible',
									},
								}
							: {
									height: 'auto',
									flexShrink: 0,
									'& .MuiChip-label': {
										whiteSpace: 'nowrap',
										paddingTop: 0.5,
										paddingBottom: 0.5,
										overflow: 'hidden',
									},
								}
					}
					label={
						<Stack
							direction={shouldUseColumnLayout ? 'column' : 'row'}
							spacing={1}
							alignItems={shouldUseColumnLayout ? 'flex-start' : 'center'}
							sx={shouldUseColumnLayout ? { minHeight: '24px', position: 'relative', width: '100%', minWidth: 0 } : { minHeight: '24px' }}
						>
							{expressionType === ExpressionTypes.Const && <Typography component="span" variant="body2" sx={{ whiteSpace: shouldUseColumnLayout ? 'pre-wrap' : 'nowrap', overflowWrap: 'anywhere', pr: shouldUseColumnLayout ? 5 : 0 }}>{String(expression?.Value)}</Typography>}
							{expressionType === ExpressionTypes.StringTemplate && <Typography component="span" variant="body2" sx={{ whiteSpace: shouldUseColumnLayout ? 'pre-wrap' : 'nowrap', overflowWrap: 'anywhere', pr: shouldUseColumnLayout ? 5 : 0 }}>{shouldUseColumnLayout ? (expression as StringTemplateExpressionModel).Content : (expression as StringTemplateExpressionModel).Content?.trim().substring(0, 120)}</Typography>}
							{expressionType === ExpressionTypes.Var && (expression as VarExpressionModel)?.Target == null && (
								<b style={{ fontFamily: codeFontFamily }}>{expression?.Value && Array.isArray(expression.Value) ? expression.Value.join('.') : expressionType}</b>
							)}
							{expressionType === ExpressionTypes.Object && <b>Object</b>}
							{expressionType === ExpressionTypes.Collection && <b>{getCollectionExpressionLabel(expression)}</b>}
							{expressionType &&
								!EXPRESSION_CHIP_LABEL_EXCLUDE.includes(expressionType) &&
								expressionType !== ExpressionTypes.Object &&
								expressionType !== ExpressionTypes.Collection &&
								(expression.Type !== ExpressionTypes.Var || (expression as VarExpressionModel)?.Target != null) && (
									<>
										<Typography component="span" variant="body2" sx={{ fontWeight: 700, color: shouldUseColumnLayout ? 'primary.main' : 'inherit', pr: shouldUseColumnLayout ? 5 : 0 }}>
											{expressionMetadata?.displayName || expressionType}
										</Typography>
										<Stack
											direction={shouldUseColumnLayout ? 'column' : 'row'}
											spacing={1}
											alignItems={shouldUseColumnLayout ? 'flex-start' : 'center'}
											sx={shouldUseColumnLayout ? { width: '100%', minWidth: 0 } : undefined}
										>
											{!shouldUseColumnLayout && <span>(</span>}
											{expressionMetadata?.parameters
												.filter((x) => x.type !== ParameterType.Activity)
												.sort(sortByOrder)
												.flatMap((param, _paramIndex, parameters) => {
													const propertyValue = expression?.[param.name];
													const paramValues = param.isCollection && param.type === ParameterType.Expression ? (propertyValue ?? []) : [propertyValue];

													// Collection params rendered by dedicated sub-component
													if (param.isCollection && param.type === ParameterType.Expression && isEditable) {
														return [
															<ExpressionPlaceholderCollectionParamEditor
																key={param.name}
																param={param}
																showLabel={shouldDisplayVerticalParameterLabel(param, parameters.length)}
																paramValues={paramValues}
																expression={expression}
																onExpressionChange={(e) => onExpressionChange?.(e)}
																itemScopes={collectionItemScopes?.get(param.name)}
																baseParamScope={parameterScopes?.get(param.name)}
																variableScope={variableScope}
																expressionScopeCalculator={expressionScopeCalculator}
																isEditable={isEditable}
																sharedContextMenuId={sharedContextMenuId}
																expectedObjectType={effectiveParameterExpectedObjectTypes.get(param.name)}
															/>,
														];
													}

													// For non-collection parameters or non-editable mode, render as before
													return paramValues.map((paramValue: ExpressionModel | string | number | boolean | undefined, i: number) => {
														const setParamValue = (newParamValue: ExpressionModel | string | number | undefined) => {
															const e: ExpressionModel = { ...expression };
															if (param.isCollection && param.type === ParameterType.Expression) {
																const v = [...paramValues];
																v[i] = newParamValue;
																e[param.name] = v;
															} else {
																e[param.name] = newParamValue;
															}
															return onExpressionChange?.(e);
														};

														const rawScope = parameterScopes?.get(param.name) ?? variableScope;
														const paramVariableScope = resolveVarTargetScope(expressionType, expression, param.name, rawScope, expressionScopeCalculator);

														// Show parameter label in column mode
														if (shouldUseColumnLayout) {
															const parameterLabel = param.displayName || param.name;

															return (
																<Stack key={`${param.name}-${i}`} direction="column" spacing={0.5} sx={{ width: '100%' }}>
																	{shouldDisplayVerticalParameterLabel(param, parameters.length) && (
																		<Typography
																			variant="caption"
																			color="text.primary"
																			title={param.description || ''}
																			sx={{ cursor: param.description ? 'help' : 'default' }}
																		>
																			{parameterLabel}:
																		</Typography>
																	)}
																	<div onClick={isEditable ? (e) => e.stopPropagation() : undefined}>
																		<ParameterPlaceholder
																			value={paramValue}
																			onValueChange={setParamValue}
																			parameterMetadata={param}
																			variableScope={paramVariableScope}
																			isEditable={isEditable}
																			contextMenuId={sharedContextMenuId}
																			expectedObjectType={effectiveParameterExpectedObjectTypes.get(param.name)}
																			model={expression}
																			fullWidth
																		/>
																	</div>
																</Stack>
															);
														}

														return (
															<div key={`${param.name}-${i}`} onClick={isEditable ? (e) => e.stopPropagation() : undefined}>
																<ParameterPlaceholder
																	value={paramValue}
																	onValueChange={setParamValue}
																	parameterMetadata={param}
																	variableScope={paramVariableScope}
																	isEditable={isEditable}
																	contextMenuId={sharedContextMenuId}
																	expectedObjectType={effectiveParameterExpectedObjectTypes.get(param.name)}
																	model={expression}
																/>
															</div>
														);
													});
												})}
											{!shouldUseColumnLayout && <span>)</span>}
										</Stack>
									</>
								)}
							{expressionChipActions}
						</Stack>
					}
				/>
			)}
			{!expression && isEditable && !showObjectEditor && !showCollectionMapEditor && !showCollectionEditor && (
				<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
					<Button variant="contained" onClick={handleExpAdd} data-context-menu-trigger="true" aria-label="Add expression">
						<AddCircleIcon />
					</Button>
					{suggestedExpressionButtons}
				</Stack>
			)}
			{expression && isEditable && Boolean(suggestedExpressions?.length) && !showObjectEditor && !showCollectionMapEditor && !showCollectionEditor && (
				<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
					{suggestedExpressionButtons}
				</Stack>
			)}
			{isEditable && !showCollectionMapEditor && (
				<ExpressionPlaceholderPopover
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					isWrapMode={isWrapMode}
					tabValue={tabValue}
					onTabChange={setTabValue}
					matchingPrimitiveTypes={matchingPrimitiveTypes}
					parameterEnum={parameterEnum}
					valueOptions={valueOptions}
					allowCustomValues={valueOptionsSource?.allowCustomValues}
					hasLoadedValueOptionsSource={hasLoadedValueOptionsSource}
					expressionPending={expressionPending}
					onExpressionPendingChange={setExpressionPending}
					variableScope={variableScope}
					parameterMetadata={parameterMetadata}
					requireCollectionVariable={isCollectionType}
					matchingExpressionMetadataList={matchingExpressionMetadataList}
					onExpressionSelected={handleExpressionSelected}
					onApply={handleApply}
				/>
			)}
			{/* Context Menu */}
			{isEditable && (
				<Menu
					open={contextMenuOpen}
					onClose={handleContextMenuClose}
					anchorEl={contextMenuAnchorEl}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					onClick={(e) => e.stopPropagation()}
				>
					<MenuItem onClick={handleWrapInExpression}>Wrap in Expression</MenuItem>
					<MenuItem disabled>Copy</MenuItem>
					<MenuItem disabled>Cut</MenuItem>
				</Menu>
			)}
		</div>
	);
}
